import { FC, ReactNode, createContext, useCallback, useState } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

type ModalConfig = {
	open: boolean;
	width: string | number | null;
	titleIcon: IconProp | null | undefined;
	title: string;
	content: ReactNode;
	resolvePromise: (value: any) => void;
	showTitle?: boolean;
};

export type ModalContextType = {
	show: (width?: string | number, titleIcon?: IconProp, title?: string, content?: ReactNode, showTitle?: boolean) => Promise<any>;
	hide: (result?: any) => void;
	modals: ModalConfig[];
};

type Props = {
	children: ReactNode;
};

export const ModalContext = createContext<ModalContextType | null>(null);

export const ModalProvider: FC<Props> = ({ children }) => {
	const [modals, setModals] = useState<ModalConfig[]>([]);

	const showModal = useCallback((width: string | number | null = null, titleIcon: IconProp | null = null, title = '', content: ReactNode | null = null, showTitle: boolean | undefined = undefined) => {
		return new Promise((resolve: any) => {
			setModals(prev => [...prev, { open: true, width, titleIcon, title, content, resolvePromise: (result: any) => resolve(result), showTitle }]);
		});
	}, []);

	const hideModal = useCallback((result?: any) => {
		// Get a reference to the last opened modal
		const currentModal = modals[modals.length - 1];

		// Resolve that modal's promise to send any info from the modal back to the parent that opened it
		if (currentModal) {
			currentModal.resolvePromise(result);
		}

		// Set the last opened modal's "open" prop to false to kick off the close animation
		setModals(prev => prev.map((modal, index) => {
			if (index === prev.length - 1) {
				return { ...modal, open: false };
			}

			return modal;
		}));

		// Wait for the close animation to finish before removing this modal from the modals array
		setTimeout(() => {
			setModals(prev => prev.slice(0, -1));
		}, 250);
	}, [modals]);

	return (
		<ModalContext.Provider value={{ show: showModal, hide: hideModal, modals }}>
			{children}
		</ModalContext.Provider>
	);
};
